import { defineStore } from "pinia";
import { ref } from "vue";
import { API } from "@/queries/index";
import { AxiosError } from "axios";
import { APIResponse, APIResponseError } from "@/types/api";
import { handleResponse, handleResponseError } from "@/utils/api";
import { ItemDataPayload, ItemDataResponse } from "@/types/interface/item";

export const useItemStore = defineStore("itemStore", () => {
  const item = ref<ItemDataResponse>();
  const items = ref<ItemDataResponse[]>([]);

  function initItems(data: any) {
    items.value = data?.data;
  }

  function addNewItem(data: any) {
    item.value = data;
  }

  async function dispatchGetItems(page = 1, limit = 10, queryParam = "") : Promise<APIResponse<null>> {
    try {
      const { status, data } = await API.item.getItem(page, limit, queryParam);
      initItems(data);
      return handleResponse(status, data);
    } catch (error) {
      const _error = error as AxiosError<APIResponseError>;
      const errorResponseData = _error.response?.data
      return handleResponseError(errorResponseData)
    }
  }

  async function dispatchGetCatalogs(page = 1, limit = 10, queryParam = "") : Promise<APIResponse<null>> {
    try {
      const { status, data } = await API.item.getCatalog(page, limit, queryParam);
      initItems(data);
      return handleResponse(status, data);
    } catch (error) {
      const _error = error as AxiosError<APIResponseError>;
      const errorResponseData = _error.response?.data
      return handleResponseError(errorResponseData)
    }
  }

  async function dispatchDetail(idItem = "") : Promise<APIResponse<null>> {
    try {
      const { status, data } = await API.item.findItem(idItem);
      addNewItem(data);
      return handleResponse(status, data);
    } catch (error) {
      const _error = error as AxiosError<APIResponseError>;
      const errorResponseData = _error.response?.data
      return handleResponseError(errorResponseData)
    }
  }

  async function dispatchCreate(payload: ItemDataPayload) : Promise<APIResponse<null>> {
    try {
      const { status, data } = await API.item.createItem(payload);
      addNewItem(data);
      return handleResponse(status, data);
    } catch (error) {
      const _error = error as AxiosError<APIResponseError>;
      const errorResponseData = _error.response?.data
      return handleResponseError(errorResponseData)
    }
  }

  async function dispatchUpdate(idItem = "", payload: ItemDataPayload) : Promise<APIResponse<null>> {
    try {
      const { status, data } = await API.item.updateItem(idItem, payload);
      addNewItem(data);
      return handleResponse(status, data);
    } catch (error) {
      const _error = error as AxiosError<APIResponseError>;
      const errorResponseData = _error.response?.data
      return handleResponseError(errorResponseData)
    }
  }

  async function dispatchDelete(idItem = "") : Promise<APIResponse<null>> {
    try {
      const { status, data } = await API.item.deleteItem(idItem);
      return handleResponse(status, data);
    } catch (error) {
      const _error = error as AxiosError<APIResponseError>;
      const errorResponseData = _error.response?.data
      return handleResponseError(errorResponseData)
    }
  }

  async function dispatchImport(payload: any) : Promise<APIResponse<null>> {
    try {
      const { status, data } = await API.item.importItem(payload);
      return handleResponse(status, data);
    } catch (error) {
      const _error = error as AxiosError<APIResponseError>;
      const errorResponseData = _error.response?.data
      return handleResponseError(errorResponseData)
    }
  }

  async function dispatchDownloadImportFormat() : Promise<APIResponse<null>> {
    try {
      const { status, data } = await API.item.downloadImportFormat();
      return handleResponse(status, data);
    } catch (error) {
      const _error = error as AxiosError<APIResponseError>;
      const errorResponseData = _error.response?.data
      return handleResponseError(errorResponseData)
    }
  }

  return {
    item,
    items,
    initItems,
    dispatchCreate,
    dispatchUpdate,
    dispatchDetail,
    dispatchGetItems,
    dispatchGetCatalogs,
    dispatchDelete,
    dispatchImport,
    dispatchDownloadImportFormat,
  };
});